import { animate, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  HostBinding,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { CsIconModule } from '@cbuild/controls';
import { FirebaseMessagingService } from '@cbuild/core';
import { FromUTCPipe } from '@cbuild/shared';
import { Subject, take, takeUntil } from 'rxjs';
import { Notifications } from '../../models/notifications.model';
import { NavMenuService } from '../nav-menu.service';
import { NotificationService } from './notification.service';

export interface PushNotification {
  collapse_key: string;
  data: {
    actionText:
      | 'Tasks'
      | 'SiteVisit'
      | 'Lead'
      | 'CustomerUnits'
      | 'CustomerPayments'
      | 'CustomerCancelUnits';
    createdDate: string;
    dueDate: string;
    infoType: string;
    isRead: string;
    notificationId: string;
    notificationMessage: string;
    notificationTitle: string;
    referenceId: string;
    userId: string;
    propertyId: number;
    associatedTo: number;
    associatedNumber: string;
  };
  from: '439081416871';
  notification: {
    body: string;
    click_action:
      | 'Tasks'
      | 'SiteVisit'
      | 'Lead'
      | 'CustomerDocuments'
      | 'CustomerPayments'
      | 'CustomerCancelUnits';
    title: string;
  };
}

@Component({
  selector: 'cs-notifications',
  templateUrl: './notifications.component.html',
  styleUrls: ['./notifications.component.scss'],
  animations: [
    trigger('notifyLeave', [
      transition(':leave', [
        style({ opacity: 1 }),
        animate('1s ease-in-out', style({ opacity: 0 }))
      ])
    ])
  ],
  imports: [CsIconModule, FromUTCPipe, RouterLink, CommonModule],
  providers: [NotificationService]
})
export class NotificationsComponent implements OnInit, OnDestroy {
  private $onDestroy = new Subject();
  @HostBinding('class.cs-nav-drop-down-opened')
  public displayDropDown = false;
  public notifications = new Array<Notifications>();
  private fetchRecords = 20;
  public notificationsCount = 0;
  public initialLoad = false;
  public showViewMore = true;

  @ViewChild('countRef', { static: true })
  private countRef!: ElementRef<HTMLSpanElement>;

  constructor(
    private notificationService: NotificationService,
    private firebaseMessagingService: FirebaseMessagingService,
    private renderer: Renderer2,
    private navMenuService: NavMenuService
  ) {}

  public ngOnInit(): void {
    this.firebaseMessagingService.messages
      .pipe(takeUntil(this.$onDestroy))
      .subscribe((msg: object) => {
        const notification = this.prepareAction([
          this.buildNotificationFromPush(msg as PushNotification) // Cast to PushNotification
        ]);
        this.notifications.unshift(...notification);
        this.getNewNotificationsCount();
      });
    this.getNewNotificationsCount();
    this.navMenuService.$openedDropDown
      .pipe(takeUntil(this.$onDestroy))
      .subscribe(val => {
        if (this !== val) {
          this.backDropClick();
        }
      });
  }

  public openDropDown(): void {
    if (!this.displayDropDown) {
      this.navMenuService.$openedDropDown.next(this);
      this.displayDropDown = true;
      if (!this.initialLoad) {
        this.notifications = [];
        this.initialLoad = true;
        const lastNotification = this.lastNotification;
        this.getNotifications(
          lastNotification ? lastNotification.notificationId : null,
          this.fetchRecords
        );
      }
    }
  }

  public notificationClick(notificationId: number): void {
    this.notificationService
      .updateNotificationsRead(notificationId)
      .pipe(take(1))
      .subscribe(_ => {
        this.notifications.filter(item => {
          if (item.notificationId === notificationId) {
            if (!item.isRead) {
              item.isRead = true;
              this.notificationsCount--;
              this.showNotificationsCount();
            }
          }
        });
      });
    this.backDropClick();
  }

  public backDropClick(): void {
    this.displayDropDown = false;
  }

  private getNewNotificationsCount(): void {
    // this.notificationService
    //   .getUnreadNotificationsCount()
    //   .pipe(take(1))
    //   .subscribe(resp => {
    //     if (resp) {
    //       this.notificationsCount = resp.count === undefined ? 0 : resp.count;
    //       this.showNotificationsCount();
    //     }
    //   });
  }

  private showNotificationsCount(): void {
    const ref = this.countRef.nativeElement;
    if (this.notificationsCount > 0) {
      this.renderer.setStyle(ref, 'display', 'block');
      this.renderer.setProperty(ref, 'textContent', this.notificationsCount);
    } else {
      this.renderer.setStyle(ref, 'display', 'none');
      this.renderer.setProperty(ref, 'textContent', '');
    }
  }

  private getNotifications(nextRef: number | null, fetchRecords: number): void {
    this.notificationService
      .getNotifications({ nextRef, fetchRecords })
      .pipe(take(1))
      .subscribe(result => {
        if (Array.isArray(result)) {
          if (result.length < 20) {
            this.showViewMore = false;
          }
          this.notifications = [
            ...this.notifications,
            ...this.prepareAction(result)
          ];
          this.updateIsNewStatus(result);
          this.getNewNotificationsCount();
        }
      });
  }

  public viewMore(): void {
    this.getNotifications(
      this.lastNotification.notificationId,
      this.fetchRecords
    );
  }

  private updateIsNewStatus(notificationsList: Array<Notifications>): void {
    if (notificationsList) {
      const notificationIdList = new Array<number>();

      notificationsList.filter(item => {
        if (!item.isRead) {
          notificationIdList.push(item.notificationId);
        }
      });

      if (notificationIdList.length > 0) {
        this.notificationService
          .updateIsNewStatus(notificationIdList)
          .pipe(take(1))
          .subscribe(_ => {
            this.getNewNotificationsCount();
          });
      }
    }
  }

  public clearNotification(index: number, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    const notificationId = this.notifications[index].notificationId;

    this.notificationService
      .clearNotification(notificationId)
      .pipe(take(1))
      .subscribe(_ => {
        this.notifications.splice(index, 1);
        this.getNewNotificationsCount();
      });
  }

  public clearAllNotifications(): void {
    this.notificationService
      .clearAllNotifications()
      .pipe(take(1))
      .subscribe(_ => {
        this.notifications = new Array<Notifications>();
        this.notificationsCount = 0;
        this.showNotificationsCount();
      });
  }

  public stopPropagation(event: Event): void {
    event.stopPropagation();
  }

  public get lastNotification(): Notifications {
    return this.notifications[this.notifications.length - 1];
  }

  public ngOnDestroy(): void {
    this.$onDestroy.next(0);
    this.$onDestroy.complete();
  }

  public buildNotificationFromPush(push: PushNotification): Notifications {
    const data = push.data;
    return {
      createdDate: data.createdDate,
      infoType: data.infoType,
      actionText: data.actionText,
      dueDate: data.dueDate,
      isRead: !data.isRead,
      notificationId:
        typeof data.notificationId === 'string'
          ? parseInt(data.notificationId, 10)
          : data.notificationId,
      notificationMessage: data.notificationMessage,
      notificationTitle: data.notificationTitle,
      referenceId:
        typeof data.referenceId === 'string'
          ? parseInt(data.referenceId, 10)
          : data.referenceId,
      userId: data.userId,
      associatedTo: data.associatedTo,
      associatedNumber: data.associatedNumber
    };
  }

  public prepareAction(
    notifications: Array<Notifications>
  ): Array<Notifications> {
    return notifications.map(_ => {
      switch (_.actionText) {
        case 'Lead':
          _.action = '/sales/activity';
          _.params = { leadId: _.referenceId };
          _.icon = 'total-leads';
          break;
        case 'Tasks':
          _.action = '/sales/activity';
          _.params = { leadId: _.associatedTo };
          _.icon = 'task-activity';
          break;
        case 'SiteVisit':
          _.action = '/sales/activity';
          _.params = { leadId: _.associatedTo };
          _.icon = 'site-visit-activity';
          break;
        case 'CustomerDocuments':
          _.action = '/crm/customers/kyc-documents/approval';
          _.params = { documentId: _.referenceId };
          _.icon = 'document-notification';
          break;
        case 'CustomerPayments':
          _.action = '/crm/customer-payments/requests/approval';
          _.params = { paymentId: _.referenceId };
          _.icon = 'payment-notification';
          break;
        case 'CustomerCancelUnits':
          _.action = '/crm/customers/cancel-requests/approval';
          _.params = { cancelUnitId: _.referenceId };
          _.icon = 'cancel-unit';
          break;
        case 'DiscountRequests':
          _.action = '/crm/customers/discount-requests/approval';
          _.params = { discountId: _.referenceId };
          _.icon = 'discount';
          break;
        case 'LeadRequest':
          _.action = '/sales/lead-requests/approval';
          _.params = { leadRequestId: _.referenceId };
          _.icon = 'lead-request';
          break;
        case 'PropertyUnitMappings':
          _.action = '/sales/inventory/view-history';
          _.params = { unitId: _.referenceId };
          _.icon = 'lead-request';
          break;
        case 'DocumentWorkFlow':
          _.action = '/site/dms/document-requests/approval';
          _.params = { requestId: _.referenceId };
          _.icon = 'site-document-approval';
          break;
        default:
      }
      return _;
    });
  }
}
